import { DoubleCheckIcon } from 'components/icons/DoubleCheck'
import { useDialogs } from 'hooks/dialogs'
import { observer } from 'mobx-react-lite'
import classNames from 'classnames'
import { ShowExperienceDialog } from '../dialogs/ShowExperienceDialog'
import { SummaryDialog } from '../dialogs/SummaryDialog'
import { useStudentAssignmentCubit } from 'hooks/cubits/studentAssignment'
import { BreakoutButton } from 'components/design-system/BreakoutButton'
import { MeetingResultsDialog } from '../dialogs/MeetingResultsDialog'
import { PeerReviewViewDialog } from '../dialogs/PeerReviewViewDialog'
import { useTranslationTyped } from 'i18n/i18n'
import { ValidLibraryObjectActionState } from '@breakoutlearning/firebase-repository/stores/ValidLibraryObject'
import { CompletedViewPeerReviewNeeded } from './CompletedViewPeerReviewNeeded'
import { type StudentPeerReviewController } from '@breakoutlearning/firebase-repository/cubits/StudentAssignment/StudentPeerReviewController'

export const CompletedView = observer(function CompletedView({
  actionState,
  peerReviewController,
}: {
  actionState: ValidLibraryObjectActionState
  peerReviewController: StudentPeerReviewController
}) {
  const cubit = useStudentAssignmentCubit()
  const { t, tt } = useTranslationTyped()
  const { showDialog } = useDialogs()

  const summary = cubit.roomStateSummary
  const canShowNewResults = true

  const isPeerReviewPending =
    actionState === ValidLibraryObjectActionState.completePeerReviews

  const assignmentTitle = cubit.assignment.slideDeck?.data.slideDeckName ?? ''

  return (
    <>
      {/* return only peer review component if needed */}
      {isPeerReviewPending && (
        <CompletedViewPeerReviewNeeded
          peerReviewController={peerReviewController}
        />
      )}
      {/* else return default view*/}
      {!isPeerReviewPending && (
        <div
          className={classNames(
            `mt-1 flex flex-1 flex-col items-center justify-center py-5 text-center`
          )}
        >
          <h3 className="text-body-medium text-grey-text">
            {t('dart.experience')}
          </h3>
          <h3 className="mt-3 text-title-large">
            <DoubleCheckIcon
              size={20}
              className="mr-1 inline-block align-top text-breakout-dark-green"
            />
            {t('dart.completed')}
          </h3>
          <div className="mt-6">
            <div className="flex flex-row flex-wrap justify-center gap-2">
              <BreakoutButton
                onClick={(e) => {
                  showDialog(
                    () => (
                      <ShowExperienceDialog
                        slideDeckId={cubit.libraryObject.slideDeckId}
                      />
                    ),
                    { trigger: e.currentTarget }
                  )
                }}
                className="inline-block whitespace-nowrap rounded-2xl bg-core-secondary px-6 py-2 text-body-large text-core-on-tertiary"
              >
                {t('student_assignment.view_experience')}
              </BreakoutButton>
              {(summary?.hasData || canShowNewResults) && (
                <BreakoutButton
                  onClick={(e) => {
                    showDialog(
                      () => {
                        if (canShowNewResults)
                          return (
                            <MeetingResultsDialog
                              roomId={cubit.roomStateId!}
                              onStudentAssignmentPage
                            />
                          )

                        return (
                          <SummaryDialog
                            summary={summary.data}
                            roomStateName={cubit.roomState.roomStateName}
                            roomStateActiveSlideChangedAt={
                              cubit.roomState.data.activeSlideChangedAt || null
                            }
                          />
                        )
                      },
                      { trigger: e.currentTarget }
                    )
                  }}
                  className="inline-block whitespace-nowrap rounded-2xl bg-core-secondary px-6 py-2 text-body-large text-core-on-tertiary"
                >
                  {t('student_assignment.view_summary')}
                </BreakoutButton>
              )}
              {peerReviewController.userIsInPeerReview &&
              peerReviewController.peerReviewsGivenByCurrentUser.value?.size ? (
                <BreakoutButton
                  kind="accent"
                  onClick={(e) => {
                    showDialog(
                      () => (
                        <PeerReviewViewDialog
                          controller={peerReviewController}
                          assignmentTitle={assignmentTitle}
                        />
                      ),
                      { trigger: e.currentTarget }
                    )
                  }}
                  className="inline-block whitespace-nowrap rounded-2xl bg-core-secondary px-6 py-2 text-body-large text-core-on-tertiary"
                >
                  {tt.student_assignment.view_peer_reviews()}
                </BreakoutButton>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </>
  )
})
